import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Grid,
  Box,
  Button,
  Typography,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Link,
} from "@mui/material";
import { TOP_APP_BAR_HEIGHT } from "../../constants/constants";
import GoogleIcon from "../../assets/google-button-icon.png";
import AppleIcon from "../../assets/apple-button-icon-black.png";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { sendPasswordResetEmail } from "../../services/authService";

const SignInForm = ({
  theme,
  pair,
  pairingCode,
  handleEmailPasswordSignIn,
  handleGoogleSignIn,
  handleAppleSignIn,
  onCodeComplete,
  loading,
  error,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const inputRefs = useRef([]);
  const isInitialCodeSet = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (pairingCode && !isInitialCodeSet.current) {
      const upperCaseCode = pairingCode.toUpperCase();
      const newCode = upperCaseCode.slice(0, 6).split("");
      setCode(newCode);
      console.log("Pairing code set from URL:", newCode);
      const firstEmptyIndex = newCode.findIndex((digit) => digit === "");
      if (firstEmptyIndex !== -1 && inputRefs.current[firstEmptyIndex]) {
        inputRefs.current[firstEmptyIndex].focus();
      }
      isInitialCodeSet.current = true;
    }
  }, [pairingCode]);

  useEffect(() => {
    if (code.every((digit) => digit !== "")) {
      const completeCode = code.join("");
      console.log("Complete code entered:", completeCode);
      onCodeComplete(completeCode);
    }
  }, [code, onCodeComplete]);

  const handleCodeInput = (index, value) => {
    const regex = /^[a-zA-Z0-9]$/;
    if (!regex.test(value) && value.length === 1) return;
    const newCode = [...code];
    newCode[index] = value.toUpperCase();
    setCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (index, e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    const newCode = [...code];
    pastedData.split("").forEach((char, i) => {
      if (index + i < 6) {
        newCode[index + i] = char.toUpperCase();
      }
    });
    setCode(newCode);
    const lastIndex = Math.min(index + pastedData.length - 1, 5);
    inputRefs.current[lastIndex]?.focus();
  };

  const handleBackspace = (index) => {
    const newCode = [...code];
    newCode[index] = "";
    setCode(newCode);
    if (index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // Forgot password handler with Snackbar alerts
  const handleForgotPassword = async () => {
    if (!email) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Please enter your email address first",
      });
      return;
    }
    try {
      await sendPasswordResetEmail(email);
      setSnackbar({
        open: true,
        severity: "success",
        message: "Password reset email sent. Please check your inbox.",
      });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to send password reset email. Please try again later.",
      });
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Container
      maxWidth="xs"
      sx={{ minHeight: `calc(100vh - ${TOP_APP_BAR_HEIGHT}px)`, px: 4, pt: 4, height: "auto" }}
    >
      <Box>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: "bold",
            fontSize: "1.3rem",
            textAlign: "left",
            mb: 2,
          }}
        >
          Sign in with Google or Apple
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Button
              onClick={handleGoogleSignIn}
              variant="contained"
              fullWidth
              sx={(theme) => ({
                border: `1px solid ${theme.colors.blue}`,
                backgroundColor: "white",
                color: "black",
                borderRadius: "50px",
                padding: "10px 40px",
                fontSize: "1.1rem",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                },
              })}
              disabled={loading}
            >
              <img
                src={GoogleIcon}
                alt="Google Icon"
                style={{
                  width: "35px",
                  position: "absolute",
                  left: "8px",
                }}
              />
              <Box sx={{ flex: 1, textAlign: "center" }}>
                {loading ? "Signing in..." : "Sign in with Google"}
              </Box>
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button
              onClick={handleAppleSignIn}
              variant="contained"
              fullWidth
              sx={(theme) => ({
                border: `1px solid ${theme.colors.blue}`,
                backgroundColor: "white",
                color: "black",
                borderRadius: "50px",
                padding: "10px 40px",
                fontSize: "1.1rem",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                },
              })}
              disabled={loading}
            >
              <img
                src={AppleIcon}
                alt="Apple Icon"
                style={{
                  width: "28px",
                  position: "absolute",
                  left: "12px",
                }}
              />
              <Box sx={{ flex: 1, textAlign: "center" }}>
                {loading ? "Signing In..." : "Sign In with Apple"}
              </Box>
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mt: 12 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: "bold",
            fontSize: "1.3rem",
            textAlign: "left",
            mb: 2,
          }}
        >
          {pair ? "Enter TV pairing code & Sign in" : "Or sign in with email"}
        </Typography>

        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {pair && (
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12}>
              <Box
                display="flex"
                justifyContent="space-between"
                gap={1}
                mb={4}
                width="100%"
              >
                {code.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleCodeInput(index, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Backspace") {
                        handleBackspace(index);
                      }
                    }}
                    onPaste={(e) => handlePaste(index, e)}
                    ref={(el) => (inputRefs.current[index] = el)}
                    style={{
                      width: "19%",
                      height: "60px",
                      textAlign: "center",
                      fontSize: "1.5rem",
                      fontWeight: "700",
                      color: "white",
                      backgroundColor: theme.colors.mediumPurple,
                      borderRadius: "12px",
                      border: `0px solid ${theme.colors.darkPurple}`,
                      outline: "none",
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              type="email"
              placeholder="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              variant="outlined"
              sx={(theme) => ({
                "& .MuiOutlinedInput-root": {
                  borderRadius: "50px",
                  paddingLeft: "20px",
                  "& fieldset": {
                    borderColor: theme.colors.blue, // Default border color
                  },
                  "&:hover fieldset": {
                    borderColor: theme.colors.blue, // Border color on hover
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.colors.blue, // Border color when focused
                  },
                },
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={togglePasswordVisibility}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={(theme) => ({
                "& .MuiOutlinedInput-root": {
                  borderRadius: "50px",
                  paddingLeft: "20px",
                  "& fieldset": {
                    borderColor: theme.colors.blue, // Default border color
                  },
                  "&:hover fieldset": {
                    borderColor: theme.colors.blue, // Border color on hover
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.colors.blue, // Border color when focused
                  },
                },
              })}
            />
          </Grid>
          <Grid item xs={12} sx={{ mb: "40px" }}>
            <Button
              onClick={() => handleEmailPasswordSignIn(email, password)}
              variant="contained"
              sx={(theme) => ({
                backgroundColor: theme.colors.blue,
                color: theme.colors.white,
                borderRadius: "50px",
                padding: "14px 40px",
                marginBottom: "25px",
                fontWeight: "bold",
                fontSize: "1rem",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                },
              })}
              disabled={loading}
            >
              {loading ? "Signing In..." : "Sign In"}
            </Button>

            <Typography align="left" sx={{ mb: 2 }}>
              <Link
                onClick={handleForgotPassword}
                underline="hover"
                sx={(theme) => ({
                  cursor: "pointer",
                  color: theme.colors.blue,
                })}
              >
                Forgot your password?
              </Link>
            </Typography>
            <Typography align="left">
              <Link
                onClick={() => navigate("/signup")}
                underline="hover"
                sx={(theme) => ({
                  cursor: "pointer",
                  color: theme.colors.blue,
                })}
              >
                Don't have Google or Apple? Sign up with your email address.
              </Link>
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SignInForm;
